import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { IconVideo } from "@tabler/icons-react";

/**
 * The card's media: the welcome video itself, muted and looping, over its
 * thumbnail. Falls back to the thumbnail (or an icon) while the manifest is
 * not playable — the processor may still be writing it — and pauses while
 * the modal plays the same video with sound.
 *
 * @param {string|null} src     HLS manifest from `welcome_video.src`
 * @param {string|null} poster  `welcome_video.thumbnail`
 * @param {boolean} [paused]    true while the player modal is open
 */
export default function Preview({ src, poster, paused = false }) {
    const videoRef = useRef(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
    }, [src]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src || failed) return undefined;

        const fail = () => setFailed(true);
        let hls = null;

        if (Hls.isSupported()) {
            hls = new Hls({
                // A thumbnail-sized loop: never fetch renditions larger than the box.
                capLevelToPlayerSize: true,
                startLevel: 0,
                enableWorker: true,
            });
            hls.on(Hls.Events.ERROR, (_, data) => {
                if (data?.fatal) fail();
            });
            hls.loadSource(src);
            hls.attachMedia(video);
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = src;
            video.addEventListener("error", fail);
        } else {
            fail();
        }

        return () => {
            hls?.destroy();
            video.removeEventListener("error", fail);
        };
    }, [src, failed]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (paused) video.pause();
        else video.play().catch(() => {});
    }, [paused]);

    if (!src || failed) {
        return poster ? (
            <img src={poster} alt="" />
        ) : (
            <span className="welcome-video__fallback" aria-hidden="true">
                <IconVideo />
            </span>
        );
    }

    return (
        <video
            ref={videoRef}
            muted
            autoPlay
            loop
            playsInline
            preload="metadata"
            poster={poster || undefined}
            aria-hidden="true"
        />
    );
}
